import { useEffect } from 'react'
import {
    pickUpPendingShadowProbes,
    processShadowProbe,
    removeRevokedShadowKeys,
    SHADOW_PROBE_EVENT,
    startLocalSigningShadow,
    startPromotedDeviceRecovery,
} from 'lib/localSigningShadow'

const PENDING_POLL_MS = 60_000

// Local signing shadow mode (lib/localSigningShadow): renders nothing. Sets up the
// device keys, answers the server's probes as they arrive, and catches up on any sent
// while this tab was asleep.
export default function LocalSigningShadowHost() {
    useEffect(() => {
        const userId = gon.currentUser?.id ? String(gon.currentUser.id) : null
        if (!userId) return undefined
        // Revoked keys go first: an enrolled user then gets fresh ones below.
        const cleanup = removeRevokedShadowKeys(
            userId,
            gon.localSigningShadowRevokedDevices || [],
        ).catch(() => {})
        if (gon.localSigningShareRecovery) {
            void cleanup.then(() => startPromotedDeviceRecovery(userId)).catch(() => {})
            return undefined
        }
        if (!gon.localSigningShadow) return undefined

        void cleanup.then(() => startLocalSigningShadow(userId)).catch(() => {})
        const onProbe = (event: Event) => {
            const probeId = (event as CustomEvent<{ id?: string }>).detail?.id
            if (probeId) void processShadowProbe(userId, probeId).catch(() => {})
        }
        const catchUp = () => {
            if (document.visibilityState === 'visible') {
                void pickUpPendingShadowProbes(userId).catch(() => {})
            }
        }
        window.addEventListener(SHADOW_PROBE_EVENT, onProbe)
        document.addEventListener('visibilitychange', catchUp)
        const timer = window.setInterval(catchUp, PENDING_POLL_MS)
        return () => {
            window.removeEventListener(SHADOW_PROBE_EVENT, onProbe)
            document.removeEventListener('visibilitychange', catchUp)
            window.clearInterval(timer)
        }
    }, [])
    return null
}
